import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  cancelOrder,
  createOrder,
  fetchAppConfig,
  fetchCategories,
  fetchManufacturers,
  fetchProducts,
  fetchUserOrders,
  submitPaymentCheck,
} from './api';
import { fetchWebSession, loginWithEmail, logoutWeb, registerWithEmail } from './auth';
import type { WebsiteAppContextValue } from './appContext';
import type { CartItemSummary, OrderResult, PaymentMethod } from './appTypes';
import { useWebTheme } from './hooks/useWebTheme';
import type {
  Category,
  Manufacturer,
  Product,
  WebSessionUser,
  AppConfig,
  OrderLocation,
} from './types';
import { sortCategoriesByWeight } from '../miniapp/utils/categorySorting';
import Header from './components/Header';
import { getLocalizedApiErrorMessage } from './utils/errorHelpers';
import { getEnabledWebsitePaymentModules } from './payments/modules';
import { getWeightPrice, cartKey, parseCartKey } from '../miniapp/utils/weight';
import {
  getLocalizedText,
  getNormalizedLanguageCode,
} from '../miniapp/i18n/localization';

const CART_STORAGE_KEY = 'tea-web-cart';

function getWebsiteEnabledPaymentMethods(
  config: AppConfig | null,
): PaymentMethod[] {
  return getEnabledWebsitePaymentModules(config).map((module) => module.code);
}

function loadInitialCart(): Record<string, number> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, number>;
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const parsedKey = parseCartKey(key);
      if (parsedKey && Number.isInteger(value) && value > 0) {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}


export default function App() {
  useWebTheme();
  const { t, i18n } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();

  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionUser, setSessionUser] = useState<WebSessionUser | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [orders, setOrders] = useState<WebsiteAppContextValue['orders']>([]);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState('');

  const [cart, setCart] = useState<Record<string, number>>(loadInitialCart);
  const [deliveryAddress, updateDeliveryAddress] = useState('');
  const [deliveryLocation, updateDeliveryLocation] = useState<OrderLocation | null>(null);
  const [phoneNumber, updatePhoneNumber] = useState('');
  const [saveAddress, setSaveAddress] = useState(true);
  const [paymentMethod, updatePaymentMethod] = useState<PaymentMethod>('IBAN');
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null);
  const [uploadingOrderId, setUploadingOrderId] = useState<number | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);

  const [loginEmail, updateLoginEmail] = useState('');
  const [loginPassword, updateLoginPassword] = useState('');
  const [authMode, setAuthMode] = useState<WebsiteAppContextValue['authMode']>('login');
  const [authNotice, setAuthNotice] = useState('');
  const [authPending, setAuthPending] = useState(false);

  const refreshSessionUser = useCallback(async (): Promise<void> => {
    try {
      const response = await fetchWebSession();
      setSessionUser(response.authenticated ? response.data : null);
    } catch {
      setSessionUser(null);
    }
  }, []);

  const resolveErrorMessage = useCallback(
    (error: unknown): string =>
      getLocalizedApiErrorMessage(error, t, {
        exists: i18n.exists.bind(i18n),
      }),
    [i18n, t],
  );

  useEffect(() => {
    let active = true;

    Promise.all([fetchProducts(), fetchCategories(), fetchManufacturers(), fetchAppConfig()])
      .then(([loadedProducts, loadedCategories, loadedManufacturers, loadedAppConfig]) => {
        if (!active) {
          return;
        }
        setProducts(loadedProducts.filter((product) => product.enabled));
        setCategories(sortCategoriesByWeight(loadedCategories));
        setManufacturers(loadedManufacturers);
        setAppConfig(loadedAppConfig);
      })
      .catch((requestError) => {
        if (!active) {
          return;
        }
        setError(resolveErrorMessage(requestError));
      })
      .finally(() => {
        if (active) {
          setCatalogLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [resolveErrorMessage]);

  useEffect(() => {
    refreshSessionUser().finally(() => setSessionLoading(false));
  }, [refreshSessionUser]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const isOrdersRoute = location.pathname === '/orders';
  const websiteEnabledPaymentMethods = useMemo(
    () => getWebsiteEnabledPaymentMethods(appConfig),
    [appConfig],
  );

  const refreshUserOrders = useCallback(async (): Promise<void> => {
    if (!sessionUser) {
      setOrders([]);
      return;
    }

    setOrdersLoading(true);
    try {
      const loadedOrders = await fetchUserOrders();
      setOrders(loadedOrders);
    } catch (requestError) {
      setError(resolveErrorMessage(requestError));
    } finally {
      setOrdersLoading(false);
    }
  }, [resolveErrorMessage, sessionUser]);

  useEffect(() => {
    if (!isOrdersRoute || !sessionUser) {
      return;
    }

    void refreshUserOrders();
  }, [isOrdersRoute, sessionUser, refreshUserOrders]);

  useEffect(() => {
    setError('');
  }, [location.pathname]);

  useEffect(() => {
    if (!websiteEnabledPaymentMethods.includes(paymentMethod)) {
      updatePaymentMethod(websiteEnabledPaymentMethods[0] ?? 'CASH_ON_DELIVERY');
    }
  }, [paymentMethod, websiteEnabledPaymentMethods]);

  const cartItems = useMemo<CartItemSummary[]>(
    () => {
      const items: CartItemSummary[] = [];
      for (const [key, quantity] of Object.entries(cart)) {
        if (quantity <= 0) {
          continue;
        }

        const parsed = parseCartKey(key);
        if (!parsed) {
          continue;
        }

        const product = products.find((entry) => entry.id === parsed.productId);
        if (!product) {
          continue;
        }

        const unitPrice = getWeightPrice(product, parsed.weight);
        if (unitPrice <= 0) {
          continue;
        }
        items.push({
          product,
          quantity,
          weight: parsed.weight,
          subtotal: Number((unitPrice * quantity).toFixed(2)),
        });
      }

      return items;
    },
    [cart, products],
  );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems],
  );
  const cartTotal = useMemo(
    () => Number(cartItems.reduce((total, item) => total + item.subtotal, 0).toFixed(2)),
    [cartItems],
  );


  const setErrorMessage = useCallback((message: string): void => {
    setError(message);
  }, []);

  const clearError = useCallback((): void => {
    setError('');
  }, []);

  function addToCart(productId: number, weight: number): void {
    const key = cartKey(productId, weight);
    setCart((current) => ({
      ...current,
      [key]: (current[key] ?? 0) + 1,
    }));
  }

  function removeFromCart(productId: number, weight: number): void {
    const key = cartKey(productId, weight);
    setCart((current) => {
      const quantity = current[key] ?? 0;
      if (quantity <= 1) {
        const next = { ...current };
        delete next[key];
        return next;
      }

      return {
        ...current,
        [key]: quantity - 1,
      };
    });
  }

  async function submitAuthForm(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError('');
    setAuthNotice('');
    setAuthPending(true);

    try {
      if (authMode === 'register') {
        const registration = await registerWithEmail(loginEmail, loginPassword);
        updateLoginPassword('');
        setAuthMode('login');
        setAuthNotice(
          registration.confirmationUrl
            ? t('website.auth.confirmationSentWithLink', {
                email: loginEmail,
                url: registration.confirmationUrl,
              })
            : t('website.auth.confirmationSent', { email: loginEmail }),
        );
        return;
      }

      const response = await loginWithEmail(loginEmail, loginPassword);
      setSessionUser(response.data);
      updateLoginPassword('');
      navigate('/');
    } catch (requestError) {
      setError(resolveErrorMessage(requestError));
    } finally {
      setAuthPending(false);
    }
  }

  async function logout(): Promise<void> {
    try {
      await logoutWeb();
      setSessionUser(null);
      navigate('/');
    } catch (requestError) {
      setError(resolveErrorMessage(requestError));
    }
  }

  async function submitCheckout(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError('');

    if (!sessionUser) {
      navigate('/login');
      return;
    }

    if (cartItems.length === 0) {
      setError(t('order.errors.cartEmpty'));
      return;
    }
    const normalizedAddress = deliveryAddress.trim();
    if (!normalizedAddress) {
      setError(t('order.errors.deliveryAddressRequired'));
      return;
    }
    const normalizedPhone = phoneNumber.trim();
    if (!normalizedPhone) {
      setError(t('order.errors.phoneRequired'));
      return;
    }
    if (!deliveryLocation) {
      setError(t('order.errors.locationRequired'));
      return;
    }

    setCheckoutPending(true);
    try {
      const response = await createOrder({
        deliveryAddress: normalizedAddress,
        phoneNumber: normalizedPhone,
        paymentMethod,
        languageCode: i18n.resolvedLanguage ?? i18n.language,
        items: cartItems.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
          weight: item.weight,
        })),
        location: deliveryLocation,
        saveAddress,
      });

      setOrderResult({
        id: response.id,
        orderNumber: response.orderNumber,
        paymentMethod: response.paymentMethod,
        paymentInstruction: response.paymentInstruction,
        total: response.total,
      });
      setCart({});
      updateDeliveryAddress('');
      updateDeliveryLocation(null);
      updatePhoneNumber('');
      setSaveAddress(true);
      updatePaymentMethod(websiteEnabledPaymentMethods[0] ?? 'CASH_ON_DELIVERY');
      navigate('/order-success');
      await refreshUserOrders();
    } catch (requestError) {
      setError(resolveErrorMessage(requestError));
    } finally {
      setCheckoutPending(false);
    }
  }

  async function cancelUserOrder(orderId: number): Promise<void> {
    setUpdatingOrderId(orderId);
    setError('');
    try {
      const updated = await cancelOrder(orderId);
      setOrders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (requestError) {
      setError(resolveErrorMessage(requestError));
    } finally {
      setUpdatingOrderId(null);
    }
  }

  async function uploadOrderPaymentCheck(
    orderId: number,
    fileList: FileList | null,
  ): Promise<void> {
    if (!fileList || fileList.length === 0) {
      return;
    }
    const file = fileList[0];
    if (!file) {
      return;
    }

    setUploadingOrderId(orderId);
    setError('');
    try {
      const updated = await submitPaymentCheck(orderId, [file]);
      setOrders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (requestError) {
      setError(resolveErrorMessage(requestError));
    } finally {
      setUploadingOrderId(null);
    }
  }

  const outletContext: WebsiteAppContextValue = {
    sessionLoading,
    sessionUser,
    products,
    categories,
    manufacturers,
    orders,
    appConfig,
    catalogLoading,
    ordersLoading,
    error,
    cartItems,
    cartCount,
    cartTotal,
    orderResult,
    deliveryAddress,
    deliveryLocation,
    phoneNumber,
    saveAddress,
    paymentMethod,
    checkoutPending,
    uploadingOrderId,
    updatingOrderId,
    loginEmail,
    loginPassword,
    authMode,
    authPending,
    authNotice,
    setError: setErrorMessage,
    clearError,
    clearOrderResult: () => setOrderResult(null),
    addToCart,
    removeFromCart,
    setDeliveryAddress: updateDeliveryAddress,
    setDeliveryLocation: updateDeliveryLocation,
    setPhoneNumber: updatePhoneNumber,
    setSaveAddress,
    setPaymentMethod: updatePaymentMethod,
    setLoginEmail: updateLoginEmail,
    setLoginPassword: updateLoginPassword,
    toggleAuthMode: () => {
      setAuthNotice('');
      setAuthMode((current) => (current === 'login' ? 'register' : 'login'));
    },
    setSessionUser,
    refreshSessionUser,
    submitAuthForm,
    logout,
    submitCheckout,
    refreshUserOrders,
    cancelUserOrder,
    uploadOrderPaymentCheck,
  };

  const isSuccessRoute = location.pathname === '/order-success';
  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);
  const footerText = getLocalizedText(
    appConfig?.websiteFooterText ?? '',
    appConfig?.websiteFooterTextTranslations,
    languageCode,
  );
  const hasFooter = Boolean(footerText || appConfig?.websiteFooterImageUrl);

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        cartCount={cartCount}
        sessionUser={sessionUser}
        logout={logout}
        logoUrl={appConfig?.websiteLogoUrl ?? null}
      />

      <main className="flex-1 w-full">
        <div className="max-w-web mx-auto px-[var(--web-side-padding)] pt-5 pb-10">
          {error && !isSuccessRoute ? <p className="text-error">{error}</p> : null}
          <Outlet context={outletContext} />
        </div>
      </main>

      {hasFooter ? (
        <footer className="px-[var(--web-side-padding)] pb-5">
          <div
            className={`relative isolate mx-auto flex min-h-[150px] max-w-web items-end overflow-hidden rounded-xl ${
              appConfig?.websiteFooterImageUrl ? 'bg-charcoal' : 'bg-secondary'
            }`}
          >
            {appConfig?.websiteFooterImageUrl ? (
              <>
                <img
                  src={appConfig.websiteFooterImageUrl}
                  alt={t('website.footer.imageAlt')}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_5%,rgba(21,19,16,0.76)_100%)]" />
              </>
            ) : null}
            {footerText ? (
              <p
                className={`relative z-[1] m-0 max-w-[65ch] p-6 text-base font-medium leading-relaxed md:px-8 ${
                  appConfig?.websiteFooterImageUrl ? 'text-milk' : 'text-text'
                }`}
              >
                {footerText}
              </p>
            ) : null}
          </div>
        </footer>
      ) : null}
    </div>
  );
}
